/**
 * Standard Upload with Progress Tracking
 * 
 * Uses XMLHttpRequest for native progress events on smaller files.
 * Simpler than TUS but doesn't support resume/pause.
 */

import type { UploadProgress, UploadController } from './types';

export interface StandardUploadOptions {
  /** File to upload */
  file: File;
  /** Full upload URL */
  url: string;
  /** HTTP headers for the request */
  headers: Record<string, string>;
  /** Callback for progress updates */
  onProgress: (progress: UploadProgress) => void;
  /** Callback when upload fails */
  onError: (error: Error) => void;
  /** Callback when upload completes */
  onSuccess: () => void;
  /** Callback when upload is aborted (prevents error toast on cancel) */
  onAbort?: () => void;
}

/**
 * Calculates upload speed using rolling average
 */
class SpeedCalculator {
  private samples: { bytes: number; timestamp: number }[] = [];
  private maxSamples = 5;

  addSample(bytesUploaded: number): number {
    const now = Date.now();
    this.samples.push({ bytes: bytesUploaded, timestamp: now });
    
    if (this.samples.length > this.maxSamples) {
      this.samples.shift();
    }
    
    if (this.samples.length < 2) return 0;
    
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const bytesDiff = last.bytes - first.bytes;
    const timeDiff = (last.timestamp - first.timestamp) / 1000;
    
    return timeDiff > 0 ? bytesDiff / timeDiff : 0;
  }

  reset(): void {
    this.samples = [];
  }
}

/**
 * Creates an upload with XHR progress tracking
 * 
 * @param options - Upload configuration
 * @returns Controller to manage the upload
 */
export function createProgressUpload(options: StandardUploadOptions): UploadController {
  const { file, url, headers, onProgress, onError, onSuccess, onAbort } = options;
  const speedCalculator = new SpeedCalculator();
  let xhr: XMLHttpRequest | null = null;
  let wasAborted = false;

  return {
    start: () => {
      wasAborted = false;
      xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          const uploadSpeed = speedCalculator.addSample(event.loaded);
          const bytesRemaining = event.total - event.loaded;
          const estimatedTimeRemaining = uploadSpeed > 0 
            ? Math.round(bytesRemaining / uploadSpeed) 
            : undefined;

          onProgress({
            bytesUploaded: event.loaded,
            bytesTotal: event.total,
            percentage,
            uploadSpeed: uploadSpeed > 0 ? uploadSpeed : undefined,
            estimatedTimeRemaining,
          });
        }
      });

      xhr.addEventListener('load', () => {
        speedCalculator.reset();
        if (xhr && xhr.status >= 200 && xhr.status < 300) {
          onSuccess();
        } else {
          onError(new Error(`Upload failed with status ${xhr?.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        speedCalculator.reset();
        onError(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        speedCalculator.reset();
        // Call onAbort if provided, otherwise don't trigger error
        if (wasAborted && onAbort) {
          onAbort();
        }
      });

      xhr.open('POST', url);
      
      // Set custom headers first
      Object.entries(headers).forEach(([key, value]) => {
        xhr?.setRequestHeader(key, value);
      });
      
      // Set Content-Type based on file type
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.send(file);
    },
    abort: () => {
      wasAborted = true;
      xhr?.abort();
      xhr = null;
      speedCalculator.reset();
    },
  };
}
